/**
 * Prize Wheel Logic Module
 *
 * Handles detection of bonus piece capture and prize wheel display logic.
 * The prize wheel appears when:
 * - Player wins the game (16+ hits on standard pieces)
 * - Winner destroyed the opponent's bonus piece
 * - At least 1 prize has been submitted by any player
 * - All prizes from both players are included in the pool
 */

/**
 * Check if player has captured (destroyed) the bonus piece
 * @param {Array<Object>} attacks - Player's attack history
 * @returns {boolean} True if bonus piece was destroyed
 */
export function hasCapturedBonusPiece(attacks) {
  if (!Array.isArray(attacks)) {
    return false;
  }

  // Look for an attack that destroyed the bonus piece
  return attacks.some(
    (attack) =>
      attack.pieceType === 'bonus' &&
      attack.pieceDestroyed === true &&
      attack.result === 'hit'
  );
}

/**
 * Determine if prize wheel should be shown
 * @param {boolean} didIWin - Whether current player won
 * @param {Array<Object>} myAttacks - Winner's attack history
 * @param {Array<string>} allPrizes - All submitted prizes from both players
 * @returns {boolean} True if prize wheel should display
 */
export function shouldShowPrizeWheel(didIWin, myAttacks, allPrizes) {
  // Must win the game
  if (!didIWin) {
    return false;
  }

  // Must have captured the bonus piece
  if (!hasCapturedBonusPiece(myAttacks)) {
    return false;
  }

  // Must have at least one prize from any player
  if (!Array.isArray(allPrizes) || allPrizes.length === 0) {
    return false;
  }

  return true;
}

/**
 * Select a random prize from all available prizes
 * @param {Array<string>} prizes - List of all prizes from both players
 * @returns {string|null} Selected prize or null if no prizes
 */
export function selectRandomPrize(prizes) {
  if (!Array.isArray(prizes) || prizes.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * prizes.length);
  return prizes[randomIndex];
}
